import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import TextStyle from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import clientConfig from '../../../client-config';

import { RichTextEditor } from 'mui-tiptap';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionPrices, setSubscriptionPrices] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [openPriceDialog, setOpenPriceDialog] = useState(false);
  const rteRef = useRef(null);
  const rteRefPriceHighlight = useRef(null);

  // **Előfizetések és árak betöltése**
  useEffect(() => {
    fetchSubscriptions();
    fetchSubscriptionPrices();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(
        `${clientConfig.backendHOST}/subscriptions/subscriptions`,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-auth-token': localStorage.token,
          },
        }
      );
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Hiba történt az előfizetések betöltésekor:', error);
    }
  };

  const fetchSubscriptionPrices = async () => {
    try {
      const response = await axios.get(
        `${clientConfig.backendHOST}/subscriptions/subscriptionPrice`,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-auth-token': localStorage.token,
          },
        }
      );
      setSubscriptionPrices(response.data);
    } catch (error) {
      console.error('Hiba történt az árak betöltésekor:', error);
    }
  };

  // **Előfizetés szerkesztése vagy hozzáadása**
  const handleSubscriptionDialogOpen = (subscription = null) => {
    setSelectedSubscription(
      subscription
        ? {
            ...subscription,
            valid_from: subscription.valid_from
              ? new Date(subscription.valid_from).toISOString().split('T')[0]
              : '',
            valid_till: subscription.valid_till
              ? new Date(subscription.valid_till).toISOString().split('T')[0]
              : '',
          }
        : {
            subscription_code: '',
            subscription_description: '',
            valid_from: '',
            valid_till: '',
            active: true,
          }
    );
    setOpenSubscriptionDialog(true);
  };

  const handleSubscriptionSave = async () => {
    try {
      if (selectedSubscription.subscription_code) {
        await axios.put(
          `${clientConfig.backendHOST}/subscriptions/subscriptions`,
          selectedSubscription,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': localStorage.token,
            },
          }
        );
      } else {
        await axios.post(
          `${clientConfig.backendHOST}/subscriptions/subscriptions`,
          selectedSubscription,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': localStorage.token,
            },
          }
        );
      }
      fetchSubscriptions();
      setOpenSubscriptionDialog(false);
    } catch (error) {
      console.error('Hiba történt az előfizetés mentésekor:', error);
    }
  };

  // **Ár szerkesztése vagy hozzáadása**
  const handlePriceDialogOpen = (price = null, subscription_code = null) => {
    setSelectedPrice(
      price || {
        subscription_code,
        valid_days: '',
        price: '',
        sort_order: '',
        active: true,
        detailed_descr: '',
      }
    );
    setOpenPriceDialog(true);
  };

  const handlePriceSave = async () => {
    setSelectedPrice({
      ...selectedPrice,
      detailed_descr: rteRef.current?.editor?.getHTML(),
      price_highlight: rteRefPriceHighlight.current?.editor?.getHTML(),
    });

    try {
      if (selectedPrice.id) {
        await axios.put(
          `${clientConfig.backendHOST}/subscriptions/subscriptionPrice`,
          {
            ...selectedPrice,
            detailed_descr: rteRef.current?.editor?.getHTML(),
            price_highlight: rteRefPriceHighlight.current?.editor?.getHTML(),
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': localStorage.token,
            },
          }
        );
      } else {
        await axios.post(
          `${clientConfig.backendHOST}/subscriptions/subscriptionPrice`,
          selectedPrice,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': localStorage.token,
            },
          }
        );
      }
      fetchSubscriptionPrices();
      setOpenPriceDialog(false);
    } catch (error) {
      console.error('Hiba történt az ár mentésekor:', error);
    }
  };

  return (
    <Box p={3} maxWidth={'lg'} mx='auto'>
      <Typography variant='h4' gutterBottom>
        Előfizetések kezelése
      </Typography>

      {/* Új előfizetés hozzáadása */}
      <Button
        variant='contained'
        color='primary'
        onClick={() => handleSubscriptionDialogOpen(null)}
        sx={{ mb: 2 }}
      >
        Új előfizetés hozzáadása
      </Button>

      {subscriptions.map((subscription) => (
        <Accordion key={subscription.subscription_code}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>
              {subscription.subscription_description}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Csomag neve</TableCell>

                    <TableCell>Időtartam (nap)</TableCell>
                    <TableCell>Ár</TableCell>
                    <TableCell>Sorrend</TableCell>
                    <TableCell>Aktív</TableCell>
                    <TableCell>Műveletek</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptionPrices
                    .filter(
                      (price) =>
                        price.subscription_code ===
                        subscription.subscription_code
                    )
                    .map((price) => (
                      <TableRow key={price.id}>
                        <TableCell>{price.price_details}</TableCell>
                        <TableCell>{price.valid_days}</TableCell>
                        <TableCell>{price.price} Ft</TableCell>
                        <TableCell>{price.sort_order}</TableCell>
                        <TableCell>{price.active ? 'Igen' : 'Nem'}</TableCell>
                        <TableCell>
                          <Button
                            variant='outlined'
                            onClick={() => handlePriceDialogOpen(price)}
                          >
                            Szerkesztés
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant='contained'
              color='secondary'
              onClick={() =>
                handlePriceDialogOpen(null, subscription.subscription_code)
              }
              sx={{ mt: 2 }}
            >
              Új ár hozzáadása
            </Button>

            <Button
              variant='contained'
              color='primary'
              onClick={() => handleSubscriptionDialogOpen(subscription)}
              sx={{ mt: 2, ml: 2 }}
            >
              Előfizetés szerkesztése
            </Button>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Előfizetés szerkesztő modal */}
      <Dialog
        open={openSubscriptionDialog}
        onClose={() => setOpenSubscriptionDialog(false)}
      >
        <DialogTitle>Előfizetés szerkesztése</DialogTitle>
        <DialogContent>
          <TextField
            label='Előfizetés kódja'
            fullWidth
            value={selectedSubscription?.subscription_code || ''}
            onChange={(e) =>
              setSelectedSubscription({
                ...selectedSubscription,
                subscription_code: e.target.value,
              })
            }
          />
          <TextField
            label='Leírás'
            fullWidth
            value={selectedSubscription?.subscription_description || ''}
            onChange={(e) =>
              setSelectedSubscription({
                ...selectedSubscription,
                subscription_description: e.target.value,
              })
            }
          />
          <TextField
            margin='dense'
            label='Érvényesség kezdete'
            type='date'
            fullWidth
            value={selectedSubscription?.valid_from || ''}
            onChange={(e) =>
              setSelectedSubscription({
                ...selectedSubscription,
                valid_from: e.target.value,
              })
            }
          />
          <TextField
            margin='dense'
            label='Érvényesség vége'
            type='date'
            fullWidth
            value={selectedSubscription?.valid_till || ''}
            onChange={(e) =>
              setSelectedSubscription({
                ...selectedSubscription,
                valid_till: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubscriptionDialog(false)}>
            Mégse
          </Button>
          <Button onClick={handleSubscriptionSave}>Mentés</Button>
        </DialogActions>
      </Dialog>

      {/* Ár szerkesztő modal */}
      <Dialog open={openPriceDialog} onClose={() => setOpenPriceDialog(false)}>
        <DialogTitle>Csomagok szerkesztése</DialogTitle>
        <DialogContent>
          <TextField
            label='Csomag neve'
            type='text'
            fullWidth
            value={selectedPrice?.price_details || ''}
            onChange={(e) =>
              setSelectedPrice({
                ...selectedPrice,
                price_details: e.target.value,
              })
            }
          />
          <TextField
            label='Időtartam (nap)'
            type='number'
            fullWidth
            value={selectedPrice?.valid_days || ''}
            onChange={(e) =>
              setSelectedPrice({ ...selectedPrice, valid_days: e.target.value })
            }
          />
          <TextField
            label='Ár'
            type='number'
            fullWidth
            value={selectedPrice?.price || ''}
            onChange={(e) =>
              setSelectedPrice({ ...selectedPrice, price: e.target.value })
            }
          />
          <TextField
            label='Sorrend'
            type='number'
            fullWidth
            value={selectedPrice?.sort_order || ''}
            onChange={(e) =>
              setSelectedPrice({ ...selectedPrice, sort_order: e.target.value })
            }
          />
          <RichTextEditor
            ref={rteRef}
            content={selectedPrice?.detailed_descr || ''} // Initial content for the editor
            // Optionally include `renderControls` for a menu-bar atop the editor:
            extensions={[
              StarterKit,
              TextStyle.configure({ mergeNestedSpanStyles: true }),
            ]} // Or any Tiptap extensions you wish!
          />
          <RichTextEditor
            ref={rteRefPriceHighlight}
            content={selectedPrice?.price_highlight || ''} // Initial content for the editor
            // Optionally include `renderControls` for a menu-bar atop the editor:
            extensions={[
              StarterKit,
              TextStyle.configure({ mergeNestedSpanStyles: true }),
            ]} // Or any Tiptap extensions you wish!
          />

          <FormControlLabel
            control={
              <Checkbox
                ref={rteRef}
                checked={selectedPrice?.active || false}
                onChange={(e) =>
                  setSelectedPrice({
                    ...selectedPrice,
                    active: e.target.checked,
                  })
                }
                color='primary'
              />
            }
            label='Aktív'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPriceDialog(false)}>Mégse</Button>
          <Button onClick={handlePriceSave}>Mentés</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscriptions;
